import React, { useState, useMemo } from 'react';
import { 
  Box, 
  CssBaseline, 
  ThemeProvider, 
  createTheme, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  IconButton,
  useTheme,
  Toolbar,
  Tooltip,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
} from '@mui/material';
import { 
  Brightness4, 
  Brightness7, 
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import Dashboard from './pages/Dashboard';
import Header from './components/Header';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Register from './pages/Register';
import Profile from './pages/Profile';
import IndividualNumbers from './pages/IndividualNumbers';
import ProtectedRoute from './components/ProtectedRoute';
import Leaderboard from './pages/Leaderboard';

const drawerWidth = 240;
const collapsedDrawerWidth = 65;

// Team color definitions
const teamColors = {
  'UNCOMMON': {
    main: '#FFB74D', // Pastel Orange
    light: '#FFE0B2',
    dark: '#F57C00',
  },
  'All Teams': {
    main: '#FFB74D', // Pastel Orange 
    light: '#FFE0B2',
    dark: '#F57C00',
  },
  'KAIZEN FINANCIAL': {
    main: '#FFD700', // Golden
    light: '#FFF64F',
    dark: '#C7A600',
  },
  'B READY': {
    main: '#42A5F5', // Blue
    light: '#90CAF9',
    dark: '#1976D2',
  },
  'HOPE FINANCIAL': {
    main: '#EC407A', // Pink
    light: '#F48FB1',
    dark: '#C2185B',
  },
};

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

// Main App component that uses Router
function App() {
  const [mode, setMode] = useState('light');
  const [selectedTeam, setSelectedTeam] = useState(() => {
    // Initialize from localStorage if available, otherwise default to UNCOMMON
    return localStorage.getItem('selectedTeam') || 'UNCOMMON';
  });

  const muiTheme = useMemo(
    () => {
      console.log('Creating theme with team:', selectedTeam); // Debug log
      return createTheme({
        palette: {
          mode,
          primary: teamColors[selectedTeam] || teamColors['UNCOMMON'],
          secondary: {
            main: '#dc004e',
          },
          background: {
            default: mode === 'light' ? '#f5f5f5' : '#121212',
            paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
          },
        },
        typography: {
          fontFamily: [
            'Archivo',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
          h1: {
            fontWeight: 700,
            letterSpacing: '0.01em',
          },
          h2: {
            fontWeight: 700,
            letterSpacing: '0.01em',
          },
          h3: {
            fontWeight: 600,
            letterSpacing: '0.01em',
          },
          h4: {
            fontWeight: 600,
            letterSpacing: '0.01em',
          },
          h5: {
            fontWeight: 500,
          },
          h6: {
            fontWeight: 500,
          },
          button: {
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
              },
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: `${teamColors[selectedTeam]?.main}20`,
                  '&:hover': {
                    backgroundColor: `${teamColors[selectedTeam]?.main}30`,
                  },
                },
              },
            },
          },
          MuiBadge: {
            styleOverrides: {
              badge: {
                backgroundColor: teamColors[selectedTeam]?.main,
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              select: {
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                },
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: `${teamColors[selectedTeam]?.main}20`,
                  '&:hover': {
                    backgroundColor: `${teamColors[selectedTeam]?.main}30`,
                  },
                },
              },
            },
          },
        },
      });
    },
    [mode, selectedTeam]
  );

  const handleTeamChange = (team) => {
    console.log('Team changed to:', team); // Debug log
    const normalizedTeam = team || 'UNCOMMON';
    console.log('Normalized team:', normalizedTeam); // Debug log
    setSelectedTeam(normalizedTeam);
    // Persist the team selection to localStorage
    localStorage.setItem('selectedTeam', normalizedTeam);
  };

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Router>
        <AppContent 
          mode={mode} 
          toggleColorMode={toggleColorMode} 
          selectedTeam={selectedTeam}
          handleTeamChange={handleTeamChange}
        />
      </Router>
    </ThemeProvider>
  );
}

// Inner component that can use Router hooks like useNavigate
function AppContent({ mode, toggleColorMode, selectedTeam, handleTeamChange }) {
  const [open, setOpen] = useState(false);
  const [activePage, setActivePage] = useState('dashboard');
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const bottomNavHeight = 56;
  const navigate = useNavigate();

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const SidebarItem = ({ icon, text, page, tooltip, onClick }) => {
    const isActive = activePage === page;
    
    return (
      <Tooltip title={tooltip} placement="right" open={!open ? undefined : false}>
        <ListItem 
          button 
          onClick={onClick || (() => {
            setActivePage(page);
            navigate(`/${page}`);
          })}
          sx={{
            position: 'relative',
            '&::after': isActive ? {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 4,
              height: '60%',
              backgroundColor: 'primary.main',
              borderRadius: '4px 0 0 4px',
            } : {},
          }}
        >
          <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit' }}>
            {icon}
          </ListItemIcon>
          {open && (
            <ListItemText 
              primary={text} 
              sx={{ 
                color: isActive ? 'primary.main' : 'inherit',
                fontWeight: isActive ? 600 : 400,
              }} 
            />
          )}
        </ListItem>
      </Tooltip>
    );
  };

  // Common page layout with sidebar
  const PageLayout = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header 
        open={open} 
        toggleDrawer={toggleDrawer} 
        team={selectedTeam} 
        isMobile={isMobile}
        mode={mode}
        toggleColorMode={toggleColorMode}
      />
      <Box sx={{ display: 'flex', flex: 1 }}>
        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
              width: open ? drawerWidth : collapsedDrawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: open ? drawerWidth : collapsedDrawerWidth,
                boxSizing: 'border-box',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflowX: 'hidden',
                marginTop: '64px',
                height: 'calc(100% - 64px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <List>
                <SidebarItem 
                  icon={<DashboardIcon />} 
                  text="Dashboard" 
                  page="dashboard" 
                  tooltip="Dashboard" 
                />
                <SidebarItem 
                  icon={<TrophyIcon />} 
                  text="Leaderboard" 
                  page="leaderboard" 
                  tooltip="Leaderboard" 
                />
              </List>
            </Box>
          </Drawer>
        )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: 'background.default',
            minHeight: '100vh',
            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: !isMobile && open ? `${drawerWidth}px` : !isMobile ? `${collapsedDrawerWidth}px` : 0,
            marginTop: '64px',
            paddingBottom: isMobile ? `${bottomNavHeight + 8}px` : 0,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          {children}
        </Box>
      </Box>
      {isMobile && (
        <BottomNavigation
          showLabels
          value={activePage}
          onChange={(e, newValue) => {
            setActivePage(newValue);
            navigate(`/${newValue}`);
          }}
          sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1201, height: bottomNavHeight }}
        >
          <BottomNavigationAction 
            label="Dashboard" 
            value="dashboard" 
            icon={<DashboardIcon />} 
          />
          <BottomNavigationAction 
            label="Leaderboard" 
            value="leaderboard" 
            icon={<TrophyIcon />} 
          />
        </BottomNavigation>
      )}
    </Box>
  );

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <PageLayout>
              <Profile />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <PageLayout>
              <Dashboard onTeamChange={handleTeamChange} />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/individual"
        element={
          <ProtectedRoute>
            <PageLayout>
              <IndividualNumbers />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <PageLayout>
              <Leaderboard onTeamChange={handleTeamChange} />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App; 